<template>
	<ui-root v-if="loaded" :data="mailAccount" :part="form" ref="frm" :errs="errors" :actions="dialogControls(env,buttons)"></ui-root>
</template>

<script setup lang="ts">
import {onMounted, onUnmounted, ref, watch, reactive, computed} from "vue";
import {injectEnv, injectEventBus, provideNewEventBus, userInRole} from "@fd/Environment";
import type {MailAccount} from "@t/mail/MailAccount";
import type {WithMeta} from "@t/fd/WithMeta";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition";
import type {ErrorMap} from "@fd/PartInjectKeys";
import {NetworkResponse} from "@fd/Types";
import {useRouter,useRoute} from "vue-router";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {type NavigateItem} from "@t/ui/Menu.ts";
import {getAttParam, getIntAttParam} from "@/router/Router.ts";
import UiRoot from "@fd/containers/UiRoot.vue";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {FormPartOrReference} from "@t/fd/FormPartOrReference.ts";
import type {Button} from "@t/fd/fixed/Button.ts";

const env = injectEnv();
const navStore = useNavigationStore();
const router = useRouter();
const route = useRoute();

const loaded = ref<boolean>(false);

const errors = reactive<ErrorMap>([]);
const mailAccount = ref<MailAccount|null>(null);
const form = ref<InterfaceRoot<FormDefinition>|null>(null);
const bus = provideNewEventBus();
const frm = ref(null);
const mailAccountId = ref(getIntAttParam(route,"id"));

function receivedData(data:MailAccount & WithMeta):void {
	if (data.META) {
		form.value = data.META.form;
		delete data.META;
	}
	mailAccount.value = data;
	loaded.value = true;
	if (mailAccount.value && mailAccount.value.id>0) {
		navStore.replace(mailAccount.value.name);
		initNavStore();
	}
}

function loadData() {
	let endpoint = null;
	if (mailAccountId.value>0) {
		endpoint = "api/mailaccounts/"+mailAccountId.value+"?meta=true";
	}
	else {
		endpoint = "api/mailaccounts/init?meta=true";
	}
	return env.net.fetch(endpoint)
			.then((result) => result.json())
			.then(receivedData);
}

const buttons = computed<FormPartOrReference[]>(() => {
		if (mailAccount.value.configStep<2) {
			return  [<Button>{ type: "button", label: env.msg('butt.continue'), action: "initdata", feedback: true }];
		}
		else if ((!mailAccount.value.id || mailAccount.value.id===0) && mailAccount.value.accountType!=='POP3') {
			return  ['continue'];
		}
		else {
			return  ['save','saveAndBack'];
		}
});

function saveData():Promise<NetworkResponse> {
    return env.net.fetch("api/mailaccounts/"+mailAccount.value.id, "PUT", mailAccount.value);
}

function createData():Promise<NetworkResponse> {
    return env.net.fetch("api/mailaccounts", "POST", mailAccount.value);
}

function initData():Promise<NetworkResponse> {
    return env.net.fetch("api/mailaccounts/init", "POST", mailAccount.value);
}

function sendData(funcName: string):Promise<NetworkResponse> {
	if (funcName === "initdata") {// Go back
		return initData();
	}
	return (!mailAccount.value.id || mailAccount.value.id===0)?createData():saveData();
}

const unregCommands = bus.onCommand(["save", "saveAndBack", "initdata", "continue"], async (cmd) => {
	errors.length = 0;
	const response = await sendData(cmd);
	if (response.ok) {
		const data = await response.json();
		receivedData(data);
		if (frm.value) {
			frm.value.setDirty(false);
		}
		if (cmd === "saveAndBack") {// Go back
			router.back();
		} else if (cmd === "initdata") {// Go back
			mailAccount.value.configStep=2;
		} else if (cmd === "continue") {// created form, do next steps
			if (mailAccount.value.accountType==='IMAP') {
				window.location.href = "./editIMAPFolders.do?isnew=true&account="+mailAccount.value.id;
			}
			else if (mailAccount.value.accountType==='GMAIL' || mailAccount.value.accountType==='OFFICE365') {
				window.location.href = "./syncWizard.do?step=refresh&mailAccount="+mailAccount.value.id;
			}
		}
		return "ok";
	} else {
		const data = await response.json();
		if (Array.isArray(data?.problems)) {
			for (let problem of data.problems) {
				errors.push(problem);
			}
		}
		console.error(response);
		return "fail";
	}
});


function initNavStore() {
	if (mailAccount.value.id>0) {
		navStore.addSideMenuGroup("menu_tools", env.msg('menu.title.tools'));	
		
		navStore.addSideMenuItemIf("menu_tools",
			<NavigateItem>{
				name: "unlimitedMailCheck",
				type: "nav",
				icon: "radar",
				title: env.msg('menu.mailAccount.unlimitedMailCheck'),
				link: "unlimitedMailCheck.do?account="+mailAccount.value.id
			}, mailAccount.value.accountType==='POP3');
				
		navStore.addSideMenuItemIf("menu_tools",
			<NavigateItem>{
				name: "editIMAPFolders",
				type: "nav",
				icon: "empty-box",
				title: env.msg('menu.editIMAPFolders'),
				link: "editIMAPFolders.do?account="+mailAccount.value.id
			}, mailAccount.value.accountType==='IMAP');
				
		navStore.addSideMenuItemIf("menu_tools",
			<NavigateItem>{
				name: "refreshAccount",
				type: "nav",
				icon: "wrench",
				title: env.msg('mailaccount.oauth.enable'),
				link: "syncWizard.do?step=refresh&mailAccount="+mailAccount.value.id
			}, mailAccount.value.accountType==='GMAIL' || mailAccount.value.accountType==='OFFICE365');
				
		navStore.addSideMenuItemIf("menu_tools",
			<NavigateItem>{
				name: "unlockCaptcha",
				type: "nav",
				icon: "wrench",
				title: env.msg('syncAccount.unlockCaptcha'),
				link: "https://www.google.com/accounts/DisplayUnlockCaptcha",
				target: "_blank"
			}, mailAccount.value.accountType==='GMAIL');
				
		navStore.addSideMenuItemIf("menu_tools",
			<NavigateItem>{
				name: "changeToGmail",
				type: "nav",
				icon: "wrench",
				title: env.msg('mailaccount.oauth.changeType.gmail'),
				link: "changeMailAccountType.do?accountType=GMAIL&mailAccount="+mailAccount.value.id,
				confirm: env.msg('confirm.changeMailAccountType')
			}, mailAccount.value.accountType==='IMAP');
			
		navStore.addSideMenuItemIf("menu_tools",
			<NavigateItem>{
				name: "changeToOffice",
				type: "nav",
				icon: "wrench",
				title: env.msg('mailaccount.oauth.changeType.office365'),
				link: "changeMailAccountType.do?accountType=OFFICE365&mailAccount="+mailAccount.value.id,
				confirm: env.msg('confirm.changeMailAccountType')
			}, mailAccount.value.accountType==='IMAP');
			
		navStore.addSideMenuItemIf("menu_tools",
			<NavigateItem>{
				name: "resyncIMAPAccount",
				type: "nav",
				icon: "refresh",
				title: env.msg('menu.resyncIMAPAccount'),
				link: "resyncIMAPAccount.do?reSync=true&account="+mailAccount.value.id,
				confirm: env.msg('menu.resyncIMAPAccount.confirm')
			}, mailAccount.value.accountType==='IMAP' && userInRole(env.user, "canAdmin"));
			
		navStore.addSideMenuItemIf("menu_tools",
			<NavigateItem>{
				name: "fullSyncIMAPAccount",
				type: "nav",
				icon: "refresh",
				title: env.msg('menu.fullsyncIMAPAccount'),
				link: "resyncIMAPAccount.do?fullSync=true&account="+mailAccount.value.id,
				confirm: env.msg('menu.resyncIMAPAccount.confirm')
			}, mailAccount.value.accountType==='IMAP');
	}
}

watch(() => mailAccountId.value, loadData, { immediate: true });

onMounted(() => {
	navStore.setStandardMenu();
	navStore.forward(env.msg("menu.createMailAccount"));
});
onUnmounted(() => {
	unregCommands();
});
</script>

<style>

</style>